import { useEffect, useState } from 'react'
import { Modal, Input, Button, Message, Spin } from '@arco-design/web-react'
import { IconLock, IconSafe } from '@arco-design/web-react/icon'
import { SecureVerificationService, type VerificationMethods } from '@/services/secureVerification'

interface SecureVerifyModalProps {
  visible: boolean
  title?: string
  onCancel: () => void
  onSuccess: () => void
}

/**
 * 敏感操作的二次验证弹窗：支持 2FA 与 Passkey
 */
export default function SecureVerifyModal({
  visible,
  title = '安全验证',
  onCancel,
  onSuccess,
}: SecureVerifyModalProps) {
  const [methods, setMethods] = useState<VerificationMethods | null>(null)
  const [loading, setLoading] = useState(false)
  const [mode, setMode] = useState<'2fa' | 'passkey'>('2fa')
  const [code, setCode] = useState('')

  useEffect(() => {
    if (!visible) {
      setCode('')
      return
    }
    setLoading(true)
    SecureVerificationService.checkAvailableVerificationMethods()
      .then((res) => {
        setMethods(res)
        if (res.has2FA) setMode('2fa')
        else if (res.hasPasskey && res.passkeySupported) setMode('passkey')
      })
      .finally(() => setLoading(false))
  }, [visible])

  const handleVerify = async () => {
    setLoading(true)
    try {
      await SecureVerificationService.verify(mode, code)
      Message.success('验证成功')
      onSuccess()
    } catch (err) {
      Message.error(err instanceof Error ? err.message : '验证失败')
    } finally {
      setLoading(false)
    }
  }

  const canUse2FA = methods?.has2FA
  const canUsePasskey = methods?.hasPasskey && methods?.passkeySupported
  const noMethod = methods && !canUse2FA && !canUsePasskey

  return (
    <Modal
      title={title}
      visible={visible}
      onCancel={onCancel}
      footer={null}
      maskClosable={false}
    >
      {loading && !methods ? (
        <div className="py-10 flex justify-center">
          <Spin />
        </div>
      ) : noMethod ? (
        <div className="py-6 text-sm text-gray-500">
          当前账号未启用 2FA 或 Passkey，请先在「个人设置」中启用后再进行敏感操作。
        </div>
      ) : (
        <div className="space-y-4">
          {canUse2FA && canUsePasskey && (
            <div className="flex gap-2">
              <Button
                type={mode === '2fa' ? 'primary' : 'outline'}
                icon={<IconLock />}
                onClick={() => setMode('2fa')}
              >
                2FA
              </Button>
              <Button
                type={mode === 'passkey' ? 'primary' : 'outline'}
                icon={<IconSafe />}
                onClick={() => setMode('passkey')}
              >
                Passkey
              </Button>
            </div>
          )}

          {mode === '2fa' ? (
            <div className="space-y-2">
              <div className="text-sm text-gray-600">请输入 6 位动态验证码或备用码</div>
              <Input
                value={code}
                onChange={setCode}
                placeholder="验证码"
                onPressEnter={handleVerify}
              />
            </div>
          ) : (
            <div className="text-sm text-gray-600">点击下方按钮触发浏览器 Passkey 验证。</div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button onClick={onCancel}>取消</Button>
            <Button type="primary" loading={loading} onClick={handleVerify}>
              验证
            </Button>
          </div>
        </div>
      )}
    </Modal>
  )
}
